import React from 'react';
import { useTranslation } from 'react-i18next';
import {
  AppButton,
  Box,
  ProgressBar,
  RoundDoneIcon,
  Text,
  useResponsive,
} from '@common-fe/common-fe';

import { ModalWrapper } from '@/components';
import { SuccessModal } from '@/components/elements';
import { GoPaperless } from '@/modules/transaction/components/Statements/components/GoPaperless';
import EditEmployeeAddressForm from '@/modules/user/components/ManageProfile/components/ManageProfileDashboard/components/EditEmployeeAddress/EditEmployeeAddressForm';

import ReturnedItemsStep from './components/ReturnedItemsStep';
import ReviewStep from './components/ReviewStep';
import useReviewAddressModal, { ReviewStep as ReviewStepEnum } from './hooks/useReviewAddressModal';

interface Props {
  storeKey?: string;
}

const ReviewAddressModal: React.FC<Props> = ({ storeKey }) => {
  const { t } = useTranslation();
  const { isMobile } = useResponsive();
  const {
    isModalVisible,
    activeStep,
    stepperOptions,
    selectedOption,
    currentAddress,
    formattedAddress,
    isReachOutEmployer,
    hasQualifyingLetters,
    termsAccepted,
    isAgreementOpened,
    isWarningShaking,
    isGoPaperlessVisible,
    isSuccessVisible,
    isNextDisabled,
    nextButtonLabel,
    stepTitle,
    successHelpText,
    setSelectedOption,
    setTermsAccepted,
    handleOpenAgreement,
    triggerWarningShake,
    handleNext,
    handleBack,
    handleClose,
    handleEditSuccess,
    handleGoPaperlessClose,
    handleGoPaperlessBack,
    handleGoPaperlessSubmit,
    handleSuccessClose,
  } = useReviewAddressModal(storeKey);

  const isFirstStep = activeStep === ReviewStepEnum.REVIEW;

  return (
    <>
      {isModalVisible && (
        <ModalWrapper visible onSetVisible={handleClose} isClosingByClickOutsideBlocked>
          <Box margin={{ bottom: isMobile ? 'spacing16' : 'spacing24' }} align="center">
            <Box width={stepperOptions.length >= 3 ? '560px' : '280px'}>
              <ProgressBar activeKey={activeStep} steps={stepperOptions} />
            </Box>
          </Box>

          <Box align="center" margin={{ bottom: isMobile ? 'spacing16' : 'spacing24' }}>
            <Text
              size={isMobile ? 'xxlarge' : '2xl'}
              color="textBody"
              weight="bold"
              textAlign="center"
              data-testid="bad-address-review_step-title"
            >
              {stepTitle}
            </Text>
          </Box>

          {activeStep === ReviewStepEnum.REVIEW && (
            <ReviewStep
              formattedAddress={formattedAddress}
              selectedOption={selectedOption}
              onSelectOption={setSelectedOption}
            />
          )}

          {activeStep === ReviewStepEnum.EDIT && currentAddress && (
            <EditEmployeeAddressForm
              address={currentAddress}
              onClose={handleBack}
              onSuccess={handleEditSuccess}
              withoutSnackbar
            />
          )}

          {activeStep === ReviewStepEnum.RETURNED_ITEMS && (
            <ReturnedItemsStep
              isReachOutEmployer={isReachOutEmployer}
              hasQualifyingLetters={hasQualifyingLetters}
              termsAccepted={termsAccepted}
              isAgreementOpened={isAgreementOpened}
              isWarningShaking={isWarningShaking}
              onOpenAgreement={handleOpenAgreement}
              onToggleTerms={() => setTermsAccepted(!termsAccepted)}
              onTriggerShake={triggerWarningShake}
            />
          )}

          {activeStep !== ReviewStepEnum.EDIT && (
            <Box
              direction={isMobile ? 'column-reverse' : 'row'}
              justify="end"
              gap="spacing12"
              margin={{ top: isMobile ? 'spacing16' : 'spacing24' }}
              pad={isMobile ? { horizontal: 'spacing4' } : undefined}
            >
              <AppButton
                testId="bad-address-review_cancel-btn"
                onClick={isFirstStep ? handleClose : handleBack}
                buttonType="secondary"
                width={isMobile ? '100%' : '180px'}
              >
                {isFirstStep ? t('Cancel') : t('Back')}
              </AppButton>
              <AppButton
                testId="bad-address-review_next-btn"
                onClick={handleNext}
                disabled={isNextDisabled}
                width={isMobile ? '100%' : '180px'}
              >
                {nextButtonLabel}
              </AppButton>
            </Box>
          )}
        </ModalWrapper>
      )}

      {isGoPaperlessVisible && (
        <GoPaperless
          onSetVisible={handleGoPaperlessClose}
          onExternalSubmit={handleGoPaperlessSubmit}
          onBack={handleGoPaperlessBack}
          isCheckboxOptional
        />
      )}

      {isSuccessVisible && (
        <SuccessModal
          icon={(
            <Box margin={{ bottom: 'spacing12' }}>
              <RoundDoneIcon />
            </Box>
          )}
          header={t("You're all set!")}
          helptext={successHelpText}
          visible
          onSetVisible={handleSuccessClose}
          buttonText={t('Close')}
          buttonStyle={{ width: isMobile ? '100%' : '200px' }}
          testId="bad-address-review_success-modal"
        />
      )}
    </>
  );
};

export default ReviewAddressModal;
